import { useState } from 'react';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { GOAL_PRIORITIES, type GoalPriority } from '../../api/types';

export const PRIORITY_CHIP_COLOR: Record<GoalPriority, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

interface Props {
  priority: GoalPriority;
  /** Given where priority may be changed in place: pressing the chip offers the others. */
  onChange?: (priority: GoalPriority) => void;
}

/** A goal's priority badge — red high, orange medium, grey low. */
export function GoalPriorityChip({ priority, onChange }: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const choose = (next: GoalPriority) => {
    setAnchor(null);
    if (next !== priority) onChange?.(next);
  };

  return (
    <>
      <Chip
        size="small"
        color={PRIORITY_CHIP_COLOR[priority]}
        label={t(`garageCar.goals.priority.${priority}`)}
        onClick={onChange ? (e) => setAnchor(e.currentTarget) : undefined}
        aria-haspopup={onChange ? 'menu' : undefined}
      />
      {onChange && (
        <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
          {GOAL_PRIORITIES.map((p) => (
            <MenuItem key={p} selected={p === priority} onClick={() => choose(p)}>
              {t(`garageCar.goals.priority.${p}`)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}
